import React, { useState, useRef } from 'react';

// Ícones SVG customizados
const MicIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

const StopIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 6h12v12H6z"/>
  </svg>
);

const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
);

const CancelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const AudioRecorder = ({ onSendAudio, maxDuration = 60 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Timer para duração
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const sendAudio = async () => {
    if (!audioBlob) return;
    
    // Converter para base64 para envio via SignalR
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Audio = reader.result.split(',')[1];
      onSendAudio({
        audioData: base64Audio,
        mimeType: 'audio/webm',
        duration: duration
      });
      cancelRecording();
    };
    reader.readAsDataURL(audioBlob);
  };

  const cancelRecording = () => {
    setAudioBlob(null);
    setDuration(0);
    chunksRef.current = [];
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Estilos
  const buttonStyle = {
    padding: '8px',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
    outline: 'none'
  };

  const micButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#4951ae',
    color: 'white',
    paddingBottom:'5px',
    '&:hover': { backgroundColor: '#1565c0' }
  };

  const stopButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#d32f2f',
    color: 'white'
  };

  const sendButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#2e7d32',
    color: 'white'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#757575',
    color: 'white'
  };

  const chipStyle = {
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex:1 }}>
      {!isRecording && !audioBlob && (
        <button onClick={startRecording} style={micButtonStyle}>
          <MicIcon />
        </button>
      )}
      
      {isRecording && (
        <>
          <button onClick={stopRecording} style={stopButtonStyle}>
            <StopIcon />
          </button>
          <span style={{
            ...chipStyle,
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            border: '1px solid #d32f2f'
          }}>
            <span style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#d32f2f',
              animation: 'pulse 1.5s infinite'
            }}></span>
            {formatDuration(duration)}
          </span>
        </>
      )}
      
      {audioBlob && !isRecording && (
        <>
          <button onClick={sendAudio} style={sendButtonStyle}>
            <SendIcon />
          </button>
          <button onClick={cancelRecording} style={cancelButtonStyle}>
            <CancelIcon />
          </button>
          <span style={{
            ...chipStyle,
            backgroundColor: 'transparent',
            color: '#666',
            border: '1px solid #ddd'
          }}>
            {formatDuration(duration)}
          </span>
        </>
      )}
      
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AudioRecorder;